import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type AccessPermission = {
  id: string;
  role: "manager" | "viewer";
};

export type UseAccessSelectionOptions = {
  permissions: AccessPermission[];
  /** When this value changes, selection resets to permissions even if content is identical. Typically the user/entity ID. */
  resetKey?: string | undefined;
  onSelectionChange?: ((selected: Map<string, "manager" | "viewer">) => void) | undefined;
};

export type UseAccessSelectionResult = {
  selected: Map<string, "manager" | "viewer">;
  handleToggle: (id: string) => void;
  handleRoleChange: (id: string, role: "manager" | "viewer") => void;
};

export function useAccessSelection({
  permissions,
  resetKey,
  onSelectionChange,
}: UseAccessSelectionOptions): UseAccessSelectionResult {
  const permissionKey = useMemo(
    () =>
      permissions
        .map((p) => `${p.id}:${p.role}`)
        .sort()
        .join(","),
    [permissions],
  );

  const stableKey = `${resetKey ?? ""}|${permissionKey}`;

  const permissionMap = useMemo(() => {
    const map = new Map<string, "manager" | "viewer">();
    for (const p of permissions) map.set(p.id, p.role);
    return map;
  }, [stableKey]); // eslint-disable-line react-hooks/exhaustive-deps -- stabilised by content+reset key

  const [selected, setSelected] = useState<Map<string, "manager" | "viewer">>(() => new Map(permissionMap));
  const lastRoleRef = useRef<Map<string, "manager" | "viewer">>(new Map(permissionMap));

  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  useEffect(() => {
    const next = new Map(permissionMap);
    setSelected(next);
    lastRoleRef.current = new Map(permissionMap);
    onSelectionChangeRef.current?.(next);
  }, [permissionMap]);

  const handleToggle = useCallback(
    (id: string) => {
      setSelected((prev) => {
        const next = new Map(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.set(id, lastRoleRef.current.get(id) ?? permissionMap.get(id) ?? "viewer");
        }
        onSelectionChangeRef.current?.(next);
        return next;
      });
    },
    [permissionMap],
  );

  const handleRoleChange = useCallback((id: string, role: "manager" | "viewer") => {
    setSelected((prev) => {
      const next = new Map(prev);
      next.set(id, role);
      lastRoleRef.current.set(id, role);
      onSelectionChangeRef.current?.(next);
      return next;
    });
  }, []);

  return { selected, handleToggle, handleRoleChange };
}
